/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, Tabs, Tab, Alert, AlertTitle, Chip, CircularProgress, Button, Accordion, AccordionSummary, AccordionDetails, Skeleton, Divider, Autocomplete, TextField } from '@mui/material';
import { formatDateTime } from '../../utilities/globalServiceUtils';
import CustomChip from '../CustomChip/CustomChip';
import { AddCircle, Delete, CopyAllRounded, Refresh, HelpOutlineOutlined } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Datagrid from '../../components/Datagrid/Datagrid';
import DatagridLoading from '../DatagridLoading/DatagridLoading';
import CreateEditUserForm from '../../components/CreateEditUserForm/CreateEditUserForm';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import TabTitle from '../TabTitle/TabTitle';
import UserName from '../UserName/UserName';
import { useAuth } from '../../context/authContext';
import ResetPasswordModal from '../../components/ResetPasswordModal/ResetPasswordModal';
import { getAllUsers } from '../../services/analyticsService';
import { deleteUsers, getEndpointData } from '../../services/adminService';
import { duplicateUser } from '../../services/manageUSersService';
import HeaderTitle from '../HeaderTitle/HeaderTitle';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { getUserCart, getProductsById } from '../../services/cartService';
import { getUserAllocations } from '../../services/manageSitesService';
import { ASSETS_BASE_URL } from '../../config';
import ContentInfo from '../ContentInfo/ContentInfo';

const USER_LIST_TABS = {
	ALL_USERS: 'all_users',
	ALL_ADMINS: 'all_admins',
	USERS_BY_SITES: 'users_by_sites',
};
const LARGE_AUTOCOMPLETE_RESULT_LIMIT = 200;
const normalizeAutocompleteSearch = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const ManageUsers = ({ showHeader = true }) => {
	const { t } = useTranslation();
	const { user: authUser, hasPermission } = useAuth();
	const toBool = (value, fallback = false) => {
		if (typeof value === 'boolean') return value;
		if (typeof value === 'number') return value === 1;
		if (typeof value === 'string') {
			const normalized = value.trim().toLowerCase();
			if (['true', '1', 'yes', 'on', 'enabled'].includes(normalized)) return true;
			if (['false', '0', 'no', 'off', 'disabled'].includes(normalized)) return false;
		}
		return fallback;
	};
	const normalizePermissionsObject = (permissions) => {
		if (!permissions) return {};
		if (typeof permissions === 'string') {
			try {
				const parsed = JSON.parse(permissions);
				return parsed && typeof parsed === 'object' ? parsed : {};
			} catch (_) {
				return {};
			}
		}
		return permissions && typeof permissions === 'object' ? permissions : {};
	};
	const actorRoleType = useMemo(
		() => String(authUser?.role?.role_type || authUser?.role?.role_name || '').trim().toLowerCase(),
		[authUser?.role?.role_type, authUser?.role?.role_name]
	);
	const actorRolePermissions = useMemo(() => normalizePermissionsObject(authUser?.role?.permissions), [authUser?.role?.permissions]);
	const actorUserManagementPerms = useMemo(() => actorRolePermissions?.user_management || {}, [actorRolePermissions]);
	const canCreateUsers = hasPermission('user_management.create_users') || (actorRoleType === 'admin' && toBool(actorUserManagementPerms?.create_users, true));
	const canEditUsers = hasPermission('user_management.edit_users') || (actorRoleType === 'admin' && toBool(actorUserManagementPerms?.edit_users, true));
	const canDeleteUsers = hasPermission('user_management.delete_users') || (actorRoleType === 'admin' && toBool(actorUserManagementPerms?.delete_users, true));
	const canManageUsers = canCreateUsers || canEditUsers || canDeleteUsers;
	const actorViewScope = useMemo(
		() => String(actorUserManagementPerms?.view_scope || (actorRoleType === 'admin' ? 'all' : 'all')).trim().toLowerCase(),
		[actorUserManagementPerms?.view_scope, actorRoleType]
	);
	const actorEditScope = useMemo(
		() => String(actorUserManagementPerms?.edit_scope || (actorRoleType === 'admin' ? 'all' : 'created_by_me')).trim().toLowerCase(),
		[actorUserManagementPerms?.edit_scope, actorRoleType]
	);
	const actorDeleteScope = useMemo(
		() => String(actorUserManagementPerms?.delete_scope || (actorRoleType === 'admin' ? 'all' : 'created_by_me')).trim().toLowerCase(),
		[actorUserManagementPerms?.delete_scope, actorRoleType]
	);
	const actorCanViewPendingUsersForApproval = useMemo(
		() => toBool(actorUserManagementPerms?.can_view_pending_users_for_approval, false),
		[actorUserManagementPerms?.can_view_pending_users_for_approval]
	);
	const actorAssignableRoleTypes = useMemo(() => {
		if (Number(authUser?.user_id) === 1) return ['admin', 'user'];
		const raw = Array.isArray(actorUserManagementPerms?.assignable_role_types)
			? actorUserManagementPerms.assignable_role_types
			: ['user'];
		return raw.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
	}, [actorUserManagementPerms?.assignable_role_types, authUser?.user_id]);
	const actorAssignableRoleTypeSet = useMemo(
		() => new Set((actorAssignableRoleTypes || []).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)),
		[actorAssignableRoleTypes]
	);
	const actorCanAssignAllRoleTypes = useMemo(
		() => actorAssignableRoleTypeSet.has('all'),
		[actorAssignableRoleTypeSet]
	);
	const BUILTIN_ASSIGNABLE_ROLE_NAMES = useMemo(
		() => new Set(['admin', 'manager', 'purchaser', 'viewer']),
		[]
	);
	const actorApprovedDomains = useMemo(() => {
		const domainScope = String(actorUserManagementPerms?.domain_user_management_scope || 'approved_domains_only').trim().toLowerCase();
		if (domainScope === 'any_domain') return [];
		const raw = String(authUser?.approved_domains || authUser?.user_meta?.approved_domains || '').trim();
		if (!raw) return [];
		return raw
			.split(',')
			.map((d) => d.trim().replace(/^@/, '').toLowerCase())
			.filter(Boolean);
	}, [authUser?.approved_domains, authUser?.user_meta?.approved_domains, actorUserManagementPerms?.domain_user_management_scope]);
	const actorSiteIds = useMemo(() => {
		const fromAllocs = Array.isArray(authUser?.allocations)
			? authUser.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
			: [];
		return Array.from(new Set(fromAllocs));
	}, [authUser?.allocations]);
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (Number(authUser?.user_id) === 1 || actorSiteIds.length > 0 || !authUser?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const resp = await getUserAllocations(authUser.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = Array.from(
					new Set(
						allocations
							.map((a) => Number(a?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (_err) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => { mounted = false; };
	}, [actorSiteIds, authUser?.user_id]);
	const effectiveActorSiteIds = useMemo(() => {
		if (actorSiteIds.length > 0) return actorSiteIds;
		return fallbackActorSiteIds;
	}, [actorSiteIds, fallbackActorSiteIds]);
	const actorHasAllSiteVisibility = useMemo(
		() => {
			if (Number(authUser?.user_id) === 1) return true;
			//  admins/managers only get "all sites" when
			// they have no explicit site allocations of their own.
			if ((actorRoleType === 'admin' || toBool(actorUserManagementPerms?.manage_all_sites, false)) && effectiveActorSiteIds.length === 0) return true;
			return false;
		},
		[authUser?.user_id, actorRoleType, actorUserManagementPerms?.manage_all_sites, effectiveActorSiteIds.length]
	);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [allUsers, setAllUsers] = useState([]);
	const [allRoles, setAllRoles] = useState([]);
	const [allCustomers, setAllCustomers] = useState([]);
	const [allSites, setAllSites] = useState([]);
	const [assignedSitesLoc, setAssignedSitesLoc] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [createNewUser, setCreateNewUser] = useState(false);
	const [selectedUsersRows, setSelectedUsersRows] = useState([]);
	const [detailUser, setDetailUser] = useState(null);
	const [detailModal, setDetailModal] = useState({ open: false, tab: 0 });
	const [userCartBySite, setUserCartBySite] = useState({});
	const [allocationRows, setAllocationRows] = useState([]);
	const [cartLoading, setCartLoading] = useState(false);
	const [cartError, setCartError] = useState('');
	const containerRef = useRef(null);
	const createFormRef = useRef(null);
	const editFormTimerRef = useRef(null);
	const pendingScrollRef = useRef(false);
	const [editFormLoading, setEditFormLoading] = useState(false);
	// Removed key-based remounts to avoid unnecessary re-renders
	const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
	const [pwdTargetUser, setPwdTargetUser] = useState(null);
	const [statusFilter, setStatusFilter] = useState({ label: 'All', value: 'all' });
	const [roleFilter, setRoleFilter] = useState([]);
	const [adminRoleFilter, setAdminRoleFilter] = useState([]);
	const [activeUsersTab, setActiveUsersTab] = useState(USER_LIST_TABS.ALL_USERS);
	const [usersBySiteCustomer, setUsersBySiteCustomer] = useState(null);
	const [usersBySiteSite, setUsersBySiteSite] = useState(null);
	const [usersBySiteEmptySites, setUsersBySiteEmptySites] = useState(false);
	const usersBySiteSearchCacheRef = useRef(new WeakMap());

  useEffect(() => {
    const fetchData = async (endpoint, setState) => {
      try {
        const response = await getEndpointData(endpoint);
		console.log("In manageusers getendpointsdata response", response);
		  if (response?.data !== undefined) {
          setState(response.data);
        }
      } catch (err) {
        setError(`${t('GENERAL.FAILED_TO_FETCH_DATA_FROM')} ${endpoint}: ${err.message}`);
      }
    };

		setLoading(true);
		Promise.all([
			fetchData('/users', setAllUsers),
			fetchData('/roles/getRoles', (rows) => {
				const payload = Array.isArray(rows?.roles)
					? rows.roles
					: Array.isArray(rows)
						? rows
						: [];
				setAllRoles(payload);
			}),
			fetchData('/cus_sites/getcustomers', setAllCustomers),
			fetchData('/cus_sites/getsites', setAllSites),
		]).finally(() => setLoading(false));
	}, []);

	// Note: Avoid forcing remounts of Datagrid on every users change
// 	useEffect(() => {
// 	setCreateNewUser(true);
// 	setSelectedUser(null);
// }, []);
  const reloadUsers = async () => {
    try {
      const response = await getAllUsers();
	  console.log("In manageusers getallusers response", response);
      if (response?.data) setAllUsers(response.data);
    } catch (e) {
      // ignore
    }
  };

	const getSiteMetaByAllocation = useCallback((allocation = {}) => {
		const normalizedSiteId = Number(allocation?.site_id ?? allocation?.siteId ?? allocation?.SiteId ?? allocation?.siteID);
		const normalizedCusId = Number(allocation?.cus_id ?? allocation?.cust_id ?? allocation?.customer_id ?? allocation?.customer?.cus_id);
		const matchedSite = !Number.isNaN(normalizedSiteId)
			? allSites.find((site) =>
				Number(site?.site_id) === normalizedSiteId &&
				(!Number.isFinite(normalizedCusId) || Number(site?.cus_id) === normalizedCusId)
			)
			: undefined;
		const fallbackNumber =
			allocation?.customer_site_number ||
			allocation?.Site ||
			allocation?.site ||
			allocation?.site_number ||
			allocation?.customer_site_number_ln ||
			"";
		const fallbackName =
			allocation?.customer_site_name ||
			allocation?.site_name ||
			allocation?.SiteName ||
			allocation?.siteName ||
			"";
		return {
			site_id: matchedSite?.site_id || normalizedSiteId || null,
			parent_customer_name: matchedSite?.parent_customer_name || allocation?.parent_customer_name || "",
			parent_customer_number: matchedSite?.parent_customer_number || allocation?.parent_customer_number || "",
			customer_site_number: matchedSite?.customer_site_number || fallbackNumber,
			customer_site_name: matchedSite?.customer_site_name || fallbackName,
		};
	}, [allSites]);

	const resolveProductImage = (product = {}, fallbackNumber = "") => {
		const rawImage =
			product?.asset_data?.images?.[0] ||
			product?.productassets?.[0]?.asset_path ||
			product?.asset_path ||
			"";
		if (rawImage) {
			if (/^https?:\/\//i.test(rawImage)) return rawImage;
			if (rawImage.startsWith("/")) return `${ASSETS_BASE_URL}${rawImage}`;
			return `${ASSETS_BASE_URL}/${rawImage}`;
		}
		if (fallbackNumber) {
			return `${ASSETS_BASE_URL}/ProductImages/${fallbackNumber}.png`;
		}
		return `${ASSETS_BASE_URL}/stock.png`;
	};

	const enrichCartProducts = useCallback(async (groupedBySite = {}) => {
		const siteEntries = Object.entries(groupedBySite);
		await Promise.all(
			siteEntries.map(async ([siteKey, items]) => {
				if (!Array.isArray(items) || items.length === 0) return;
				const productIds = Array.from(
					new Set(
						items
							.map((item) => item?.ProductID || item?.product_id)
							.filter((id) => id !== undefined && id !== null && id !== "")
					)
				);
				if (!productIds.length) return;
				try {
					const response = await getProductsById(productIds, siteKey);
					const productList = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
					const productMap = new Map(
						productList.map((product) => [String(product?.product_id ?? product?.productid), product])
					);
					groupedBySite[siteKey] = items.map((item, idx) => {
						const product = productMap.get(String(item?.ProductID)) || item?.product_data || {};
						const productNumber = product?.product_number || item?.ProductNumber || item?.ProductID || "";
						const productName = product?.product_name || item?.ProductName || product?.name || "";
						return {
							...item,
							__id: item?.__id || `${siteKey}-${item?.ProductID || idx}`,
							ProductName: productName,
							ProductNumber: productNumber,
							imageUrl: resolveProductImage(product, productNumber),
							productData: product,
						};
					});
				} catch (error) {
					console.error("Failed to fetch cart product details", error);
				}
			})
		);
	}, []);

	const loadUserCart = useCallback(async (userId) => {
		if (!userId) {
			setUserCartBySite({});
			return;
		}
		setCartLoading(true);
		setCartError('');
		setUserCartBySite({});
		try {
			const response = await getUserCart(userId);
			const rawItems = Array.isArray(response?.data) ? response.data : [];
			const grouped = rawItems.reduce((acc, item, index) => {
				const siteKey = String(item?.Site || item?.site || item?.site_number || '').trim();
				if (!siteKey) return acc;
				const normalizedItem = {
					...item,
					__id: item?.id ? `${siteKey}-${item.id}` : `${siteKey}-${index}`,
					ProductName: item?.ProductName || item?.product_name || item?.Product || item?.name || '',
					ProductID: item?.ProductID || item?.product_id,
					Quantity: item?.Quantity || item?.quantity || 0,
					Site: siteKey,
					site_id: item?.site_id || item?.siteId || item?.SiteId || null,
					cus_id: item?.cus_id || item?.CusId || null,
				};
				if (!acc[siteKey]) acc[siteKey] = [];
				acc[siteKey].push(normalizedItem);
				return acc;
			}, {});
			await enrichCartProducts(grouped);
			setUserCartBySite(grouped);
		} catch (e) {
			console.error("Failed to load user cart", e);
			setCartError(e?.message || t('ALERT_MESSAGES.ERROR'));
			setUserCartBySite({});
		} finally {
			setCartLoading(false);
		}
	}, [t]);

	const handleRowSelect = (rows) => {
		if (!Array.isArray(rows)) return setSelectedUsersRows([]);
		// If rows are full objects, extract IDs
		const ids = rows.map((r) => (typeof r === 'object' ? r.user_id : r));
		setSelectedUsersRows(ids);
	};

	const handleOpenPasswordModal = (row) => {
		setPwdTargetUser(row);
		setPwdDialogOpen(true);
	};

	const handleDeleteRows = async () => {
		if (selectedUsersRows.length === 0) {
			Swal.fire({ icon: 'warning', title: t('ALERT_MESSAGES.ERROR'), text: 'No users selected' });
			return;
		}
		const selectedIdSet = new Set((selectedUsersRows || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)));
		const selectedRowsData = (activeTabRows || []).filter((row) => selectedIdSet.has(Number(row?.user_id)));
		const validUserIds = selectedRowsData
			.filter((row) => canShowDeleteForRow(row) && Number(row?.user_id) !== Number(authUser?.user_id))
			.map((row) => row.user_id);
		if (!validUserIds.length) {
			Swal.fire({
				icon: 'warning',
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('USER_MANAGEMENT.NO_VALID_USERS_SELECTED_FOR_DELETE', { defaultValue: 'No valid users are selected for deletion.' }),
			});
			return;
		}
		if (validUserIds.length !== selectedIdSet.size) {
			const proceed = await Swal.fire({
				icon: 'warning',
				title: t('TITLES.CONFIRM_DELETION'),
				html: t('USER_MANAGEMENT.ONLY_ELIGIBLE_SELECTED_USERS_WILL_BE_DELETED', {
					count: validUserIds.length,
					defaultValue: `Only <b>${validUserIds.length}</b> eligible selected user(s) can be deleted. Continue?`,
				}),
				showCancelButton: true,
				confirmButtonText: t("UI_TEXT.TOOLTIP_DELETE"),
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!proceed.isConfirmed) return;
		} else {
			const confirm = await Swal.fire({
				icon: 'warning',
				title: t('TITLES.CONFIRM_DELETION'),
				html: `You are about to delete <b>${validUserIds.length}</b> user(s). This action will remove their data and sessions.`,
				showCancelButton: true,
				confirmButtonText: t("UI_TEXT.TOOLTIP_DELETE"),
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!confirm.isConfirmed) return;
		}
		try {
			const resp = await deleteUsers({ userIds: validUserIds });
			console.log("In manageusers deleteusers response", resp);
			setAllUsers(resp?.data || []);
			setSelectedUsersRows([]);
			if (selectedUser && validUserIds.includes(selectedUser.user_id)) {
				setSelectedUser(null);
			}
			if (detailUser && validUserIds.includes(detailUser.user_id)) {
				closeUserDetailModal();
			}
			Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.USERS_DELETED') });
		} catch (e) {
			Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: e.message });
		}
	};

	// Stable rows reference to avoid re-renders and effects
	const usersRows = React.useMemo(() => {
		if (!canManageUsers) return [];
		return allUsers.filter((u) => u.user_id !== 1);
	}, [allUsers, canManageUsers]);
	const canAssignRoleByPermission = useCallback((role) => {
		const roleType = String(role?.role_type || '').trim().toLowerCase();
		if (roleType !== 'admin') return true;
		const rolePerms = normalizePermissionsObject(role?.permissions);
		const actorPerms = actorRolePermissions && typeof actorRolePermissions === 'object' ? actorRolePermissions : {};
		const actorUserMgmt = actorPerms?.user_management || {};
		const roleUserMgmt = rolePerms?.user_management || {};
		const toScopeLevel = (scopeValue = 'created_by_me') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'all' ? 2 : 1;
		};
		const toDomainScopeLevel = (scopeValue = 'approved_domains_only') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'any_domain' ? 2 : 1;
		};
		const toSiteAllocationScopeLevel = (scopeValue = 'specific_only') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'all_or_specific' ? 2 : 1;
		};
		const restrictsWhenTrue = new Set([
			'user_management.restrict_email_to_approved_domains',
		]);
		const hasTargetBooleanOutsideActor = (targetNode, actorNode, pathPrefix = '') => {
			if (!targetNode || typeof targetNode !== 'object') return false;
			return Object.keys(targetNode).some((key) => {
				const path = pathPrefix ? `${pathPrefix}.${key}` : key;
				const targetValue = targetNode[key];
				const actorValue = actorNode && typeof actorNode === 'object' ? actorNode[key] : undefined;
				if (typeof targetValue === 'boolean') {
					if (restrictsWhenTrue.has(path)) {
						// Reverse semantics for restrictive booleans: false is broader than true.
						return targetValue === false && actorValue === true;
					}
					return targetValue === true && actorValue !== true;
				}
				if (targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
					return hasTargetBooleanOutsideActor(targetValue, actorValue, path);
				}
				return false;
			});
		};

		const actorAssignable = Array.isArray(actorUserMgmt?.assignable_role_types)
			? actorUserMgmt.assignable_role_types.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
			: ['user'];
		const roleAssignable = Array.isArray(roleUserMgmt?.assignable_role_types)
			? roleUserMgmt.assignable_role_types.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
			: ['user'];
		if (roleAssignable.some((v) => !actorAssignable.includes(v))) return false;
		if (toScopeLevel(roleUserMgmt?.view_scope || 'all') > toScopeLevel(actorUserMgmt?.view_scope || 'all')) return false;
		if (toScopeLevel(roleUserMgmt?.edit_scope || 'created_by_me') > toScopeLevel(actorUserMgmt?.edit_scope || 'created_by_me')) return false;
		if (toScopeLevel(roleUserMgmt?.delete_scope || 'created_by_me') > toScopeLevel(actorUserMgmt?.delete_scope || 'created_by_me')) return false;
		if (toDomainScopeLevel(roleUserMgmt?.domain_user_management_scope || 'approved_domains_only') > toDomainScopeLevel(actorUserMgmt?.domain_user_management_scope || 'approved_domains_only')) return false;
		if (toSiteAllocationScopeLevel(roleUserMgmt?.site_allocation_scope || 'specific_only') > toSiteAllocationScopeLevel(actorUserMgmt?.site_allocation_scope || 'specific_only')) return false;
		if (hasTargetBooleanOutsideActor(rolePerms, actorPerms)) return false;
		return true;
	}, [actorRolePermissions]);
	const roleFilterOptions = React.useMemo(() => {
		const visibleRoleNames = new Set((usersRows || []).map((u) => String(u?.user_role_name || '').trim()).filter(Boolean));
		if (authUser?.user_id === 1) {
			return (allRoles || [])
				.filter((r) => visibleRoleNames.has(String(r?.role_name || '').trim()))
				.sort((a, b) => {
					const typeCmp = String(a?.role_type || '').localeCompare(String(b?.role_type || ''));
					if (typeCmp !== 0) return typeCmp;
					return String(a?.role_name || '').localeCompare(String(b?.role_name || ''));
				});
		}
		const assignableTypeSet = new Set((actorAssignableRoleTypes || []).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean));
		const canAssignAllTypes = assignableTypeSet.has('all');
		return (allRoles || [])
			.filter((r) => visibleRoleNames.has(String(r?.role_name || '').trim()))
			.filter((r) => {
				const roleType = String(r?.role_type || '').trim().toLowerCase();
				if (!canAssignAllTypes && !assignableTypeSet.has(roleType)) return false;
				return canAssignRoleByPermission(r);
			})
			.sort((a, b) => {
				const typeCmp = String(a?.role_type || '').localeCompare(String(b?.role_type || ''));
				if (typeCmp !== 0) return typeCmp;
				return String(a?.role_name || '').localeCompare(String(b?.role_name || ''));
			});
	}, [usersRows, allRoles, authUser?.user_id, actorAssignableRoleTypes, canAssignRoleByPermission]);
	useEffect(() => {
		if (!Array.isArray(roleFilter) || roleFilter.length === 0) return;
		const validIds = new Set((roleFilterOptions || []).map((r) => Number(r?.role_id)).filter((id) => Number.isFinite(id)));
		const next = roleFilter.filter((r) => validIds.has(Number(r?.role_id)));
		if (next.length !== roleFilter.length) {
			setRoleFilter(next);
		}
	}, [roleFilter, roleFilterOptions]);
	const isPermissionValueEnabled = useCallback((value) => {
		if (typeof value === 'boolean') return value;
		if (typeof value === 'number') return value > 0;
		if (typeof value === 'string') {
			const normalized = value.trim().toLowerCase();
			return ['true', '1', 'yes', 'on', 'enabled', 'all', 'any_domain', 'approved_domains_only', 'created_by_me', 'assigned_sites'].includes(normalized);
		}
		if (Array.isArray(value)) return value.length > 0;
		return false;
	}, []);
	const scopeRank = useCallback((value, path = '') => {
		const normalized = String(value || '').trim().toLowerCase();
		if (!normalized) return 0;
		if (path.endsWith('domain_user_management_scope')) {
			if (normalized === 'approved_domains_only') return 1;
			if (normalized === 'any_domain') return 2;
			return 0;
		}
		if (['none', 'created_by_me', 'assigned_sites', 'all'].includes(normalized)) {
			return { none: 0, created_by_me: 1, assigned_sites: 2, all: 3 }[normalized];
		}
		return isPermissionValueEnabled(value) ? 1 : 0;
	}, [isPermissionValueEnabled]);
	const roleHasHigherPermissionsThanActor = useCallback((candidatePermissions, actorPermissions, pathPrefix = '') => {
		if (!candidatePermissions || typeof candidatePermissions !== 'object') return false;
		const entries = Object.entries(candidatePermissions);
		for (const [key, candidateValRaw] of entries) {
			const path = pathPrefix ? `${pathPrefix}.${key}` : key;
			const actorValRaw = actorPermissions?.[key];
			const candidateIsObject = candidateValRaw && typeof candidateValRaw === 'object' && !Array.isArray(candidateValRaw);
			if (candidateIsObject) {
				if (roleHasHigherPermissionsThanActor(candidateValRaw, actorValRaw || {}, path)) return true;
				continue;
			}
			if (Array.isArray(candidateValRaw)) {
				const candidateSet = new Set(candidateValRaw.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean));
				const actorSet = new Set((Array.isArray(actorValRaw) ? actorValRaw : []).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean));
				for (const item of candidateSet) {
					if (!actorSet.has(item)) return true;
				}
				continue;
			}
			const candidateVal = candidateValRaw;
			const actorVal = actorValRaw;
			const candidateScope = scopeRank(candidateVal, path);
			const actorScope = scopeRank(actorVal, path);
			if (candidateScope > actorScope) return true;
			const candidateEnabled = isPermissionValueEnabled(candidateVal);
			const actorEnabled = isPermissionValueEnabled(actorVal);
			if (candidateEnabled && !actorEnabled) return true;
		}
		return false;
	}, [isPermissionValueEnabled, scopeRank]);
	const roleTypeById = useMemo(() => {
		const map = new Map();
		(allRoles || []).forEach((r) => {
			const id = Number(r?.role_id);
			const type = String(r?.role_type || '').trim().toLowerCase();
			if (Number.isFinite(id) && type) map.set(id, type);
		});
		return map;
	}, [allRoles]);
	const roleTypeByName = useMemo(() => {
		const map = new Map();
		(allRoles || []).forEach((r) => {
			const key = String(r?.role_name || '').trim().toLowerCase();
			const type = String(r?.role_type || '').trim().toLowerCase();
			if (key && type) map.set(key, type);
		});
		return map;
	}, [allRoles]);
	const getRowRoleType = useCallback((row) => {
		const direct = String(row?.role_type || row?.user_role_type || '').trim().toLowerCase();
		if (direct) return direct;
		const roleId = Number(row?.user_role);
		if (Number.isFinite(roleId) && roleTypeById.has(roleId)) return roleTypeById.get(roleId);
		const roleNameKey = String(row?.user_role_name || row?.user_role || '').trim().toLowerCase();
		if (roleNameKey && roleTypeByName.has(roleNameKey)) return roleTypeByName.get(roleNameKey);
		return '';
	}, [roleTypeById, roleTypeByName]);
	const isRowBuiltinAssignableRole = useCallback((row) => {
		const roleId = Number(row?.user_role ?? row?.role_id);
		if (Number.isFinite(roleId) && roleId >= 1 && roleId <= 4) return true;
		const roleName = String(row?.user_role_name || row?.role_name || '').trim().toLowerCase();
		return !!roleName && BUILTIN_ASSIGNABLE_ROLE_NAMES.has(roleName);
	}, [BUILTIN_ASSIGNABLE_ROLE_NAMES]);
	const getRowEmailDomain = useCallback((row) => {
		const email = String(row?.user_email || '').trim().toLowerCase();
		if (!email || !email.includes('@')) return '';
		return email.split('@').pop() || '';
	}, []);
	const isRowWithinActorDomainScope = useCallback((row) => {
		return true;
	}, []);
	const canAccessCreatedScopeRow = useCallback((row) => {
		return Number(row?.created_by) === Number(authUser?.user_id);
	}, [authUser?.user_id]);
	const getRowSiteIds = useCallback((row) => {
		const fromAllocs = Array.isArray(row?.allocations)
			? row.allocations
				.map((a) => Number(a?.site_id))
				.filter((id) => Number.isFinite(id) && id > 0)
			: [];
		if (fromAllocs.length > 0) return Array.from(new Set(fromAllocs));
		const fallback = Array.isArray(row?.user_sites)
			? row.user_sites.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
			: [];
		return Array.from(new Set(fallback));
	}, []);
	const isRowManageableBySiteScope = useCallback((row) => {
		if (actorHasAllSiteVisibility) return true;
		const rowSiteIds = getRowSiteIds(row);
		if (rowSiteIds.length === 0) {
			if (Array.isArray(actorApprovedDomains) && actorApprovedDomains.length > 0) {
				return isRowWithinActorDomainScope(row);
			}
			return false;
		}
		const actorSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
		return rowSiteIds.every((id) => actorSet.has(Number(id)));
	}, [actorHasAllSiteVisibility, getRowSiteIds, effectiveActorSiteIds, actorApprovedDomains, isRowWithinActorDomainScope]);
	const canShowEditForRow = useCallback((row) => {
		if (!canEditUsers) return false;
		if (Number(authUser?.user_id) === 1) return true;
		if (!row) return false;
		if (!isRowWithinActorDomainScope(row)) return false;
		if (!isRowManageableBySiteScope(row)) return false;

		if (actorEditScope === 'created_by_me' && !canAccessCreatedScopeRow(row)) {
			return false;
		}

		return true;
	}, [canEditUsers, authUser?.user_id, actorEditScope, isRowWithinActorDomainScope, isRowManageableBySiteScope, canAccessCreatedScopeRow]);
	const canShowDeleteForRow = useCallback((row) => {
		if (!canDeleteUsers) return false;
		if (Number(authUser?.user_id) === 1) return true;
		if (!row) return false;
		if (!isRowWithinActorDomainScope(row)) return false;
		if (!isRowManageableBySiteScope(row)) return false;

		if (actorDeleteScope === 'created_by_me' && Number(row?.created_by) !== Number(authUser?.user_id)) {
			return false;
		}
		return true;
	}, [canDeleteUsers, authUser?.user_id, actorDeleteScope, isRowWithinActorDomainScope, isRowManageableBySiteScope]);
	const canShowDuplicateForRow = useCallback((row) => {
		if (!canCreateUsers) return false;
		if (!row) return false;
		if (Number(row?.user_id) === 1) return false;
		if (!isRowWithinActorDomainScope(row)) return false;
		if (!isRowManageableBySiteScope(row)) return false;

		if (actorRoleType === 'admin' && isRowBuiltinAssignableRole(row)) {
			return true;
		}
		if (actorCanAssignAllRoleTypes) {
			return true;
		}

		const targetRoleType = getRowRoleType(row);
		if (targetRoleType && actorAssignableRoleTypeSet.size > 0 && !actorAssignableRoleTypeSet.has(targetRoleType)) {
			return false;
		}
		return true;
	}, [
		canCreateUsers,
		isRowManageableBySiteScope,
		getRowRoleType,
		actorAssignableRoleTypeSet,
		isRowWithinActorDomainScope,
		actorRoleType,
		isRowBuiltinAssignableRole,
		actorCanAssignAllRoleTypes,
	]);
	const canViewRowByScope = useCallback((row) => {
		if (!row) return false;
		if (Number(authUser?.user_id) === 1) return true;
		const isPending = Number(row?.user_status) === 0;
		if (isPending && !actorCanViewPendingUsersForApproval) return false;
		if (!isRowWithinActorDomainScope(row)) return false;
		if (Number(row?.user_id) === Number(authUser?.user_id)) return true;

		if (isPending && actorCanViewPendingUsersForApproval) return true;

		if (!isRowManageableBySiteScope(row)) return false;
		if (actorViewScope === 'created_by_me') {
			return canAccessCreatedScopeRow(row);
		}
		return true;
	}, [actorViewScope, authUser?.user_id, isRowWithinActorDomainScope, isRowManageableBySiteScope, canAccessCreatedScopeRow, actorCanViewPendingUsersForApproval]);
	const viewScopedUsersRows = React.useMemo(
		() => (usersRows || []).filter((row) => canViewRowByScope(row)),
		[usersRows, canViewRowByScope]
	);
	const isAdminUserRow = useCallback((row) => {
		if (!row) return false;
		const roleType = getRowRoleType(row);
		if (roleType) return roleType === 'admin';
		return String(row?.user_role_name || '').trim().toLowerCase() === 'admin';
	}, [getRowRoleType]);
	const usersBySiteCustomerOptions = useMemo(() => (
		(allCustomers || []).filter((customer) => customer?.customer_name && customer.customer_name !== 'EMPTY')
	), [allCustomers]);
	const scopedUsersBySiteSiteOptions = useMemo(() => {
		if (actorHasAllSiteVisibility) return allSites || [];
		const allowedSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
		return (allSites || []).filter((site) => allowedSet.has(Number(site?.site_id)));
	}, [actorHasAllSiteVisibility, allSites, effectiveActorSiteIds]);
	const scopedUsersBySiteCustomerOptions = useMemo(() => {
		if (actorHasAllSiteVisibility) return usersBySiteCustomerOptions;
		const allowedCustomerIds = new Set(
			(scopedUsersBySiteSiteOptions || [])
				.map((site) => Number(site?.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		return (usersBySiteCustomerOptions || []).filter((customer) => allowedCustomerIds.has(Number(customer?.cus_id)));
	}, [actorHasAllSiteVisibility, scopedUsersBySiteSiteOptions, usersBySiteCustomerOptions]);
	const usersBySiteSiteOptions = useMemo(() => {
		if (usersBySiteCustomer?.cus_id) {
			return (scopedUsersBySiteSiteOptions || []).filter((site) => Number(site?.cus_id) === Number(usersBySiteCustomer.cus_id));
		}
		return scopedUsersBySiteSiteOptions || [];
	}, [scopedUsersBySiteSiteOptions, usersBySiteCustomer?.cus_id]);
	const usersBySiteFilterOptions = useCallback((options = [], { inputValue } = {}) => {
		const normalizedInput = normalizeAutocompleteSearch(inputValue);
		if (!normalizedInput) return options.slice(0, LARGE_AUTOCOMPLETE_RESULT_LIMIT);
		const filtered = [];
		for (const option of options) {
			let searchable = usersBySiteSearchCacheRef.current.get(option);
			if (searchable === undefined) {
				searchable = normalizeAutocompleteSearch(
					`${option?.customer_name || ''} ${option?.customer_number || ''} ${option?.customer_site_name || ''} ${option?.customer_site_number || ''}`
				);
				usersBySiteSearchCacheRef.current.set(option, searchable);
			}
			if (searchable.includes(normalizedInput)) filtered.push(option);
			if (filtered.length >= LARGE_AUTOCOMPLETE_RESULT_LIMIT) break;
		}
		return filtered;
	}, []);
	useEffect(() => {
		if (usersBySiteCustomer && !scopedUsersBySiteCustomerOptions.some((customer) => Number(customer?.cus_id) === Number(usersBySiteCustomer?.cus_id))) {
			setUsersBySiteCustomer(null);
			setUsersBySiteSite(null);
		}
	}, [usersBySiteCustomer, scopedUsersBySiteCustomerOptions]);
	useEffect(() => {
		if (usersBySiteSite && !usersBySiteSiteOptions.some((site) => Number(site?.site_id) === Number(usersBySiteSite?.site_id))) {
			setUsersBySiteSite(null);
		}
	}, [usersBySiteSite, usersBySiteSiteOptions]);
	useEffect(() => {
		if (activeUsersTab !== USER_LIST_TABS.USERS_BY_SITES) return;
		if (!usersBySiteCustomer) {
			setUsersBySiteEmptySites(false);
			return;
		}
		setUsersBySiteEmptySites(usersBySiteSiteOptions.length === 0);
	}, [activeUsersTab, usersBySiteCustomer, usersBySiteSiteOptions.length]);
	const applyStatusAndRoleFilters = useCallback((rows = [], { includeRoleFilter = true, selectedRoles = roleFilter } = {}) => {
		let filtered = Array.isArray(rows) ? [...rows] : [];
		if (statusFilter?.value && statusFilter.value !== 'all') {
			filtered = filtered.filter((u) => (Number(u?.user_status) === 0 ? 'pending' : 'approved') === statusFilter.value);
		}
		if (includeRoleFilter && Array.isArray(selectedRoles) && selectedRoles.length > 0) {
			const selectedRoleNames = new Set(selectedRoles.map((r) => String(r?.role_name || '')));
			filtered = filtered.filter((u) => selectedRoleNames.has(String(u?.user_role_name || '')));
		}
		filtered.sort((a, b) => {
			const statusCmp = Number(a?.user_status || 0) - Number(b?.user_status || 0);
			if (statusCmp !== 0) return statusCmp;
			const aTime = a?.updated ? new Date(a.updated).getTime() : 0;
			const bTime = b?.updated ? new Date(b.updated).getTime() : 0;
			return bTime - aTime;
		});
		return filtered;
	}, [statusFilter, roleFilter]);
	const allUsersTabRows = useMemo(
		() => applyStatusAndRoleFilters(viewScopedUsersRows, { includeRoleFilter: true }),
		[applyStatusAndRoleFilters, viewScopedUsersRows]
	);
	const allAdminsBaseRows = useMemo(
		() => (viewScopedUsersRows || []).filter((row) => isAdminUserRow(row)),
		[isAdminUserRow, viewScopedUsersRows]
	);
	const adminRoleFilterOptions = useMemo(() => {
		const visibleAdminRoleNames = new Set((allAdminsBaseRows || []).map((u) => String(u?.user_role_name || '').trim()).filter(Boolean));
		return (allRoles || [])
			.filter((role) => String(role?.role_type || '').trim().toLowerCase() === 'admin')
			.filter((role) => visibleAdminRoleNames.has(String(role?.role_name || '').trim()))
			.filter((role) => {
				if (Number(authUser?.user_id) === 1) return true;
				const roleType = String(role?.role_type || '').trim().toLowerCase();
				if (!actorCanAssignAllRoleTypes && !actorAssignableRoleTypeSet.has(roleType)) return false;
				return canAssignRoleByPermission(role);
			})
			.sort((a, b) => String(a?.role_name || '').localeCompare(String(b?.role_name || '')));
	}, [allAdminsBaseRows, allRoles, authUser?.user_id, actorCanAssignAllRoleTypes, actorAssignableRoleTypeSet, canAssignRoleByPermission]);
	useEffect(() => {
		if (!Array.isArray(adminRoleFilter) || adminRoleFilter.length === 0) return;
		const validIds = new Set((adminRoleFilterOptions || []).map((r) => Number(r?.role_id)).filter((id) => Number.isFinite(id)));
		const next = adminRoleFilter.filter((r) => validIds.has(Number(r?.role_id)));
		if (next.length !== adminRoleFilter.length) {
			setAdminRoleFilter(next);
		}
	}, [adminRoleFilter, adminRoleFilterOptions]);
	const allAdminsTabRows = useMemo(
		() => applyStatusAndRoleFilters(allAdminsBaseRows, { includeRoleFilter: true, selectedRoles: adminRoleFilter }),
		[allAdminsBaseRows, applyStatusAndRoleFilters, adminRoleFilter]
	);
	const usersBySiteBaseRows = useMemo(() => {
		if (!usersBySiteCustomer && !usersBySiteSite) return [];
		const selectedSiteId = Number(usersBySiteSite?.site_id);
		if (Number.isFinite(selectedSiteId) && selectedSiteId > 0) {
			return (viewScopedUsersRows || []).filter((row) => {
				const rowSiteIds = getRowSiteIds(row);
				if (rowSiteIds.length === 0) return true;
				return rowSiteIds.includes(selectedSiteId);
			});
		}
		const selectedCustomerId = Number(usersBySiteCustomer?.cus_id);
		if (Number.isFinite(selectedCustomerId) && selectedCustomerId > 0) {
			const selectedCustomerSiteIds = new Set(
				(scopedUsersBySiteSiteOptions || [])
					.filter((site) => Number(site?.cus_id) === selectedCustomerId)
					.map((site) => Number(site?.site_id))
					.filter((id) => Number.isFinite(id) && id > 0)
			);
			return (viewScopedUsersRows || []).filter((row) => {
				const rowSiteIds = getRowSiteIds(row);
				if (rowSiteIds.length === 0) return true;
				return rowSiteIds.some((id) => selectedCustomerSiteIds.has(Number(id)));
			});
		}
		return [];
	}, [usersBySiteCustomer, usersBySiteSite, viewScopedUsersRows, getRowSiteIds, scopedUsersBySiteSiteOptions]);
	const usersBySiteTabRows = useMemo(
		() => applyStatusAndRoleFilters(usersBySiteBaseRows, { includeRoleFilter: true }),
		[applyStatusAndRoleFilters, usersBySiteBaseRows]
	);
	const activeTabRows = useMemo(() => {
		if (activeUsersTab === USER_LIST_TABS.ALL_ADMINS) return allAdminsTabRows;
		if (activeUsersTab === USER_LIST_TABS.USERS_BY_SITES) return usersBySiteTabRows;
		return allUsersTabRows;
	}, [activeUsersTab, allAdminsTabRows, usersBySiteTabRows, allUsersTabRows]);
	const allUsersCount = useMemo(() => viewScopedUsersRows.length, [viewScopedUsersRows]);
	const allAdminsCount = useMemo(() => allAdminsBaseRows.length, [allAdminsBaseRows]);
	const statusOptions = React.useMemo(() => ([
		{ label: 'All', value: 'all' },
		{ label: 'Pending', value: 'pending' },
		{ label: 'Approved', value: 'approved' },
	]), []);
	const statusFilterControl = React.useMemo(() => (
		<Autocomplete
			size="small"
			options={statusOptions}
			value={statusFilter}
			onChange={(_, value) => setStatusFilter(value || statusOptions[0])}
			getOptionLabel={(option) => option?.label || ''}
			isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={t('USER_MANAGEMENT.FILTER_BY_STATUS', { defaultValue: 'Filter By Status' })}
				/>
			)}
			sx={{ minWidth: 190 }}
		/>
	), [statusFilter, statusOptions, t]);
	const roleFilterControl = React.useMemo(() => (
		<Autocomplete
			size="small"
			options={roleFilterOptions}
			value={roleFilter}
			multiple
			disableCloseOnSelect
			onChange={(_, value) => setRoleFilter(Array.isArray(value) ? value : [])}
			groupBy={(option) => option?.role_type || "Other"}
			getOptionLabel={(option) => option?.role_name || ''}
			isOptionEqualToValue={(option, value) => Number(option?.role_id) === Number(value?.role_id)}
			renderOption={(props, option) => (
				<li {...props}>
					<Box sx={{ display: 'flex', flexDirection: 'column' }}>
						<Typography sx={{ fontSize: 12, fontWeight: 500 }}>{option?.role_name || ''}</Typography>
					</Box>
				</li>
			)}
			renderGroup={(params) => (
				<li key={params.key}>
					<Box sx={{ px: 1, py: 0.5, fontSize: 12, fontWeight: 600, backgroundColor: '#eee' }}>
						Role Type: {params.group}
					</Box>
					<ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>{params.children}</ul>
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={t('USER_MANAGEMENT.FILTER_BY_ROLE', { defaultValue: 'Filter By Role' })}
				/>
			)}
			slotProps={{
				chip: {
					size: 'small',
				},
			}}
			sx={{ minWidth: 230 }}
		/>
	), [roleFilter, roleFilterOptions, t]);
	const adminRoleFilterControl = React.useMemo(() => (
		<Autocomplete
			size="small"
			options={adminRoleFilterOptions}
			value={adminRoleFilter}
			multiple
			disableCloseOnSelect
			onChange={(_, value) => setAdminRoleFilter(Array.isArray(value) ? value : [])}
			getOptionLabel={(option) => option?.role_name || ''}
			isOptionEqualToValue={(option, value) => Number(option?.role_id) === Number(value?.role_id)}
			renderOption={(props, option) => (
				<li {...props}>
					<Box sx={{ display: 'flex', flexDirection: 'column' }}>
						<Typography sx={{ fontSize: 12, fontWeight: 500 }}>{option?.role_name || ''}</Typography>
					</Box>
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={t('USER_MANAGEMENT.FILTER_BY_ROLE', { defaultValue: 'Filter By Role' })}
				/>
			)}
			slotProps={{
				chip: {
					size: 'small',
				},
			}}
			sx={{ minWidth: 230 }}
		/>
	), [adminRoleFilter, adminRoleFilterOptions, t]);
	const usersBySiteLabel = useMemo(() => {
		if (usersBySiteEmptySites) return t('LABELS.NO_SITES_FOUND_CUSTOMER', { defaultValue: 'No sites found for selected customer' });
		if (!usersBySiteCustomer) return t('LABELS.CUSTOMER_VIEW_LOCATIONS', { defaultValue: 'Customer view locations' });
		if (usersBySiteSiteOptions.length === 0) return t('LABELS.CUSTOMER_VIEW_LOCATIONS', { defaultValue: 'Customer view locations' });
		return t('LABELS.SELECT_CUSTOMER_SITE', { defaultValue: 'Select Customer Site' });
	}, [t, usersBySiteCustomer, usersBySiteEmptySites, usersBySiteSiteOptions.length]);
	const allUsersTabLabel = useMemo(
		() => t('USER_MANAGEMENT.ALL_USERS_WITH_COUNT', { count: allUsersCount, defaultValue: `All Users (${allUsersCount})` }),
		[t, allUsersCount]
	);
	const allAdminsTabLabel = useMemo(
		() => t('USER_MANAGEMENT.ALL_ADMINS_WITH_COUNT', { count: allAdminsCount, defaultValue: `All Admins (${allAdminsCount})` }),
		[t, allAdminsCount]
	);
	const usersBySitesTabLabel = useMemo(
		() => t('USER_MANAGEMENT.USERS_BY_SITES', { defaultValue: 'Users By Sites' }),
		[t]
	);
	const usersBySitesFiltersLabel = useMemo(
		() => t('UI_TEXT.FILTERS_LABEL', { defaultValue: 'Filters:' }),
		[t]
	);
	const usersBySitesEmptySelectionMessage = useMemo(
		() => t('USER_MANAGEMENT.SELECT_CUSTOMER_OR_SITE_TO_VIEW_USERS', { defaultValue: 'Please select a customer and/or site to view the matching users.' }),
		[t]
	);
	const usersBySitesControls = React.useMemo(() => (
		<>
			<Autocomplete
				size="small"
				options={scopedUsersBySiteCustomerOptions}
				getOptionLabel={(option) => option?.customer_name || ''}
				value={usersBySiteCustomer}
				onChange={(_, newValue) => {
					setUsersBySiteCustomer(newValue || null);
					setUsersBySiteSite(null);
				}}
				filterOptions={usersBySiteFilterOptions}
				isOptionEqualToValue={(option, value) => Number(option?.cus_id) === Number(value?.cus_id)}
				renderInput={(params) => (
					<TextField {...params} label={t('LABELS.SELECT_A_CUSTOMER')} />
				)}
				fullWidth
			/>
			<Autocomplete
				size="small"
				options={usersBySiteSiteOptions}
				getOptionLabel={(option) =>
					`${option?.customer_site_name || ''} - ${option?.customer_site_number || ''}`.trim()
				}
				value={usersBySiteSite}
				onChange={(_, newValue) => {
					setUsersBySiteSite(newValue || null);
					if (newValue?.cus_id) {
						const parentCustomer = scopedUsersBySiteCustomerOptions.find((customer) => Number(customer?.cus_id) === Number(newValue.cus_id));
						if (parentCustomer) setUsersBySiteCustomer(parentCustomer);
					}
				}}
				filterOptions={usersBySiteFilterOptions}
				isOptionEqualToValue={(option, value) => Number(option?.site_id) === Number(value?.site_id)}
				renderInput={(params) => (
					<TextField
						{...params}
						label={usersBySiteLabel}
						placeholder={t('LABELS.CUSTOMER_VIEW_LOCATIONS')}
					/>
				)}
				fullWidth
				disabled={usersBySiteSiteOptions.length === 0}
			/>
		</>
	), [usersBySiteCustomer, scopedUsersBySiteCustomerOptions, usersBySiteLabel, usersBySiteSite, usersBySiteSiteOptions, t, usersBySiteFilterOptions]);
	const quickFilters = React.useMemo(() => (
		<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
			{statusFilterControl}
			{activeUsersTab === USER_LIST_TABS.ALL_ADMINS ? adminRoleFilterControl : roleFilterControl}
		</Box>
	), [activeUsersTab, roleFilterControl, adminRoleFilterControl, statusFilterControl]);
	const defaultSort = React.useMemo(() => [
		{ field: 'user_status', sort: 'asc' },
		{ field: 'updated', sort: 'desc' },
	], []);
	const visibilityNote = React.useMemo(() => {
		if (!canManageUsers) return '';
		if (Number(authUser?.user_id) === 1) {
			return t('USER_MANAGEMENT.VISIBILITY_NOTE_SUPER_ADMIN', {
				defaultValue: 'You can see all users across all sites. Edit and delete actions still depend on your role permissions.',
			});
		}
		const actorRoleType = String(authUser?.role?.role_type || authUser?.role?.role_name || '').trim().toLowerCase();
		const actorAllocations = Array.isArray(authUser?.allocations) ? authUser.allocations : [];
		const fallbackSites = String(authUser?.user_sites || '')
			.split(',')
			.map((v) => Number(String(v || '').trim()))
			.filter((id) => Number.isFinite(id) && id > 0);
		const assignedSiteCount = new Set([
			...actorAllocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0),
			...fallbackSites,
		]).size;
		const hasAllSiteVisibility = actorRoleType === 'admin' && assignedSiteCount === 0;
		const siteScopeText = hasAllSiteVisibility
			? t('USER_MANAGEMENT.VISIBILITY_SITE_SCOPE_ALL_SITES', { defaultValue: 'all sites' })
			: t('USER_MANAGEMENT.VISIBILITY_SITE_SCOPE_ASSIGNED_SITES', { defaultValue: 'your assigned sites' });
		const userScopeText = actorViewScope === 'created_by_me'
			? t('USER_MANAGEMENT.VISIBILITY_USER_SCOPE_CREATED_BY_ME', { defaultValue: 'only users created by you' })
			: t('USER_MANAGEMENT.VISIBILITY_USER_SCOPE_ALL_IN_SCOPE', { defaultValue: 'all users in scope' });
		return t('USER_MANAGEMENT.VISIBILITY_NOTE_SCOPED', {
			userScopeText,
			siteScopeText,
			defaultValue: 'You are seeing {{userScopeText}} within {{siteScopeText}}. Edit and delete actions may still be more limited based on your role settings.',
		});
	}, [canManageUsers, authUser?.user_id, authUser?.role?.role_type, authUser?.role?.role_name, authUser?.allocations, authUser?.user_sites, actorViewScope, t]);

  const handleDeleteSingle = async (user_id, row) => {
    if (authUser && Number(authUser.user_id) === Number(user_id)) {
      Swal.fire({ icon: 'warning', title: t('ALERT_MESSAGES.ERROR'), text: t('GENERAL.YOU_CANNOT_DELETE_YOUR_OWN_ACCOUNT') });
      return;
    }
    const name = row?.user_display_name || `${row?.user_first_name || ''} ${row?.user_last_name || ''}`.trim();
    const confirm = await Swal.fire({
      icon: 'warning',
      title: t('TITLES.CONFIRM_DELETION'),
      html: t('TITLES.CONFIRM_DELETE_USER', { name: name || user_id }),
      showCancelButton: true,
      confirmButtonText: t('BUTTON.DELETE'),
      cancelButtonText: t('BUTTON.CANCEL'),
    });
    if (!confirm.isConfirmed) return;
    try {
      const resp = await deleteUsers({ userIds: [user_id] });
	  console.log("In manageusers deleteusers response", resp);
      setAllUsers(resp?.data || []);
			if (selectedUser?.user_id === user_id) {
				setSelectedUser(null);
			}
			if (detailUser?.user_id === user_id) {
				closeUserDetailModal();
			}
      Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.USERS_DELETED') });
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: e.message });
    }
  };

  const handleDuplicateUser = async (row) => {
	  if (!canShowDuplicateForRow(row)) {
		  Swal.fire({
			  icon: 'error',
			  title: t('ALERT_MESSAGES.ERROR'),
			  text: 'You cannot duplicate this user because their email domain, role type, or site allocations are outside your allowed scope.',
		  });
		  return;
	  }
    const name = row?.user_display_name || `${row?.user_first_name || ''} ${row?.user_last_name || ''}`.trim();
    const confirm = await Swal.fire({
      icon: 'question',
      title: t('UI_TEXT.DUPLICATE_USER_TITLE'),
      html: `${t('UI_TEXT.CREATE_COPY_OF')} <b>${name || row.user_email}</b>? ${t('GENERAL.PASSWORD_WILL_BE_CLEARED_AND_STATUS_SET_TO_PENDING')}`,
      showCancelButton: true,
      confirmButtonText: t('UI_TEXT.DUPLICATE'),
      cancelButtonText: t('BUTTON.CANCEL'),
    });
    if (!confirm.isConfirmed) return;
    try {
      const resp = await duplicateUser(row.user_id);
	  console.log("In manageusers duplicateuser response", resp);
      if (resp.status) {
        await reloadUsers();
        Swal.fire({ icon: 'success', title: t('GENERAL.USER_DUPLICATED') });
      } else {
        throw new Error(resp?.data?.message || t('GENERAL.FAILED_TO_DUPLICATE_USER'));
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: e.message });
    }
  };

	const openUserDetailModal = useCallback((row) => {
		if (!row) return;
		setDetailUser(row);
		setDetailModal({ open: true, tab: 0 });
		loadUserCart(row.user_id);
		const allocations = Array.isArray(row.allocations) ? row.allocations : [];
		const rows = allocations
			.map((allocation, index) => {
				const meta = getSiteMetaByAllocation(allocation);
				const idSource = meta.customer_site_number || meta.site_id || index;
				return {
					id: `${idSource}-${index}`,
					...meta,
				};
			})
			.filter((row) => row.customer_site_number || row.customer_site_name);
		setAllocationRows(rows);
	}, [getSiteMetaByAllocation, loadUserCart]);

	const closeUserDetailModal = () => {
		setDetailModal({ open: false, tab: 0 });
		setDetailUser(null);
		setUserCartBySite({});
		setCartError('');
		setAllocationRows([]);
	};

	const deriveSiteKey = (allocation = {}) => {
		const candidates = [
			allocation?.customer_site_number,
			allocation?.site_number,
			allocation?.siteid,
			allocation?.site_id,
			allocation?.Site,
			allocation?.site,
		];
		const key = candidates.find((val) => val !== undefined && val !== null && val !== "");
		return key ? String(key) : "";
	};

	const deriveSiteLabel = (allocation = {}, fallback = "") => allocation?.customer_site_name || allocation?.site_name || fallback;

	// use global formatters

	const timeAgo = (dateString) => {
		if (!dateString) return '-';
		const now = new Date();
		const then = new Date(dateString);
		const diffMs = now - then;
		if (Number.isNaN(diffMs)) return formatDateTime(dateString);
		const sec = Math.floor(diffMs / 1000);
		const min = Math.floor(sec / 60);
		const hr = Math.floor(min / 60);
		const day = Math.floor(hr / 24);
		if (sec < 60) return `${sec}s ago`;
		if (min < 60) return `${min}m ago`;
		if (hr < 24) return `${hr}h ago`;
		if (day < 7) return `${day}d ago`;
		return formatDateTime(dateString);
	};

	const handleEdit = (row, shouldScroll = false) => {
		if (!row) return;
		pendingScrollRef.current = shouldScroll;
		setCreateNewUser(false);
		console.log("Editing User: ", row);
		const allocations = Array.isArray(row.allocations) ? row.allocations : [];
		const fallbackSiteIds = Array.isArray(row?.user_sites)
			? row.user_sites
			: String(row?.user_sites || '')
				.split(',')
				.map((id) => Number(id))
				.filter((id) => Number.isFinite(id) && id > 0);
		const derivedAllocations =
			allocations.length > 0
				? allocations
				: fallbackSiteIds
					.map((siteId) => {
						const matchedSite = allSites.find((site) => Number(site?.site_id) === Number(siteId));
						if (!matchedSite) return null;
						return {
							cus_id: Number(matchedSite?.cus_id),
							site_id: Number(matchedSite?.site_id),
						};
					})
					.filter((entry) => Number.isFinite(entry?.cus_id) && Number.isFinite(entry?.site_id));
		const assign = [];
		const byCus = derivedAllocations.reduce((acc, a) => {
			const customerId = Number(a?.cus_id);
			const siteId = Number(a?.site_id);
			if (!Number.isFinite(customerId) || !Number.isFinite(siteId)) return acc;
			acc[customerId] = acc[customerId] || [];
			acc[customerId].push(siteId);
			return acc;
		}, {});
		Object.keys(byCus).forEach((cid) =>
			assign.push({
				customer: Number(cid),
				sites: Array.from(new Set(byCus[cid].map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))),
			})
		);

		if (editFormTimerRef.current) {
			clearTimeout(editFormTimerRef.current);
		}
		setSelectedUser(null);
		setEditFormLoading(true);
		editFormTimerRef.current = setTimeout(() => {
			setAssignedSitesLoc(assign);
			setSelectedUser({ ...row, ...(row.user_meta || {}) });
			setEditFormLoading(false);
			if (pendingScrollRef.current) {
				handleScroll();
				pendingScrollRef.current = false;
			}
		}, 500);
	};

	const handleScroll = () => {
		containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const columns = React.useMemo(() => [
		{
			field: "user_display_name",
			headerName: t('TABLE_HEADERS.NAME'),
			flex: 1.5,
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%', width: '100%' }}>
					<UserName
						size={30}
						name={params.row.user_display_name || `${params.row.user_first_name || ''} ${params.row.user_last_name || ''}`.trim()}
					/>
				</Box>
			),
		},
		{
			field: "user_email",
			headerName: t('TABLE_HEADERS.EMAIL'),
			flex: 3,
			renderCell: (params) => {
				const isPending = Number(params?.row?.user_status) === 0;
				const rawLastLogin = params?.row?.user_meta?.last_login || params?.row?.last_login || null;
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%', width: '100%' }}>
						<Typography variant="body2" sx={{ fontSize: 12 }}>
							{params?.row?.user_email || 'N/A'}
							{isPending && (
								<Typography component="span" sx={{ fontSize: 11, fontWeight: "bold", color: 'error.main' }}>
									{" "}- Pending Approval
								</Typography>
							)}
						</Typography>

						<Typography variant="body2" sx={{ fontSize: "10px", fontStyle: "italic", color: 'text.secondary' }}>
							{`Last login: ${rawLastLogin ? timeAgo(rawLastLogin) : 'N/A'}`}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: "user_role_name", headerName: t('LABELS.ROLE'), align: "center",
			headerAlign: 'center',
		},
		{
			field: "user_status",
			align: "center",
			headerAlign: 'center',
			headerName: t('TABLE_HEADERS.STATUS'),
			flex: 1,
			renderCell: (params) => {
				const isPending = Number(params.row.user_status) === 0;
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
						<CustomChip text={isPending ? 'Pending' : 'Approved'} color={isPending ? 'warning' : 'success'} title={'Status'} />
					</Box>

				);
			},
		},
		{
			field: "eula_accepted",
			headerName: t('GENERAL.EULA_ACCEPTED'),
			align: 'center',
			headerAlign: 'center',
			renderCell: (params) => {
				const raw = params.row?.eula_accepted ?? params.row?.user_meta?.eula_accepted;
				const val = (String(raw || '').toLowerCase() === 'true' || raw === true || String(raw) === '1');
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
						<CustomChip text={val ? 'Yes' : 'No'} color={val ? 'success' : 'error'} title={'EULA'} />
					</Box>
				)


			},
		},
		{
			field: "updated",
			headerName: t('TABLE_HEADERS.LAST_MODIFIED_ON', { defaultValue: 'Last Modified On' }),
			flex: 1,
			align: "center",
			headerAlign: 'center',
			valueFormatter: (value) => formatDateTime(value),
		},
		{
			field: "updated_by_display_name",
			headerName: t('TABLE_HEADERS.LAST_MODIFIED_BY', { defaultValue: 'Last Modified By' }),
			flex: 1.2,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
					<UserName size={24} name={params.row.updated_by_display_name || 'N/A'} />
				</Box>
			),
		},
		{
			headerName: t('TABLE_HEADERS.ACTIONS'),
			sortable: false,
			width: 200,
			headerAlign: "center",
			align: "left",
			renderCell: (params) => {
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>

						<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
							{(() => {
								const editAllowed = canShowEditForRow(params.row);
								return (
									<Tooltip title={editAllowed ? t('UI_TEXT.EDIT_USER') : t('UI_TEXT.OUT_OF_SITE_SCOPE', { defaultValue: 'This user has a broader or non-overlapping site scope than your account.' })}>
										<span>
											<IconButton
												color="primary"
												aria-label="Edit User"
												disabled={!editAllowed}
												onClick={(event) => {
													event.stopPropagation();
													if (selectedUser) setSelectedUser(null);
													handleEdit(params.row, true);
												}}>
												<EditIcon />
											</IconButton>
										</span>
									</Tooltip>
								);
							})()}
							{(() => {
								const duplicateAllowed = canShowDuplicateForRow(params.row);
								return (
									<Tooltip title={duplicateAllowed ? t('UI_TEXT.DUPLICATE') : t('UI_TEXT.OUT_OF_SITE_SCOPE', { defaultValue: 'This user has a broader or non-overlapping site scope.' })}>
										<span>
											<IconButton
												color="primary"
												disabled={!duplicateAllowed}
												onClick={(event) => {
													event.stopPropagation();
													handleDuplicateUser(params.row);
												}}>
												<CopyAllRounded />
											</IconButton>
										</span>
									</Tooltip>
								);
							})()}
							{(() => {
								const editAllowed = canShowEditForRow(params.row);
								return (
									<Tooltip title={editAllowed ? t('UI_TEXT.SEND_PASSWORD_RESET_LINK') : t('UI_TEXT.OUT_OF_SITE_SCOPE', { defaultValue: 'This user has a broader or non-overlapping site scope than your account.' })}>
										<span>
											<IconButton
												color="primary"
												aria-label="Send Reset"
												disabled={!editAllowed}
												onClick={(event) => {
													event.stopPropagation();
													handleOpenPasswordModal(params.row);
													handleScroll()
												}}>
												<VpnKeyIcon />
											</IconButton>
										</span>
									</Tooltip>
								);
							})()}
							{(() => {
								const deleteAllowed = canShowDeleteForRow(params.row);
								return (
									<Tooltip title={deleteAllowed ? t("UI_TEXT.TOOLTIP_DELETE") : t('UI_TEXT.OUT_OF_SITE_SCOPE', { defaultValue: 'This user has a broader or non-overlapping site scope.' })}>
										<span>
											<IconButton
												color="error"
												disabled={!deleteAllowed}
												onClick={(event) => {
													event.stopPropagation();
													handleDeleteSingle(params.row.user_id, params.row);
												}}>
												<Delete />
											</IconButton>
										</span>
									</Tooltip>
								);
							})()}
						</Box>
					</Box>
				)
			},
		},
	], [t, canCreateUsers, canEditUsers, canDeleteUsers, canShowEditForRow, canShowDeleteForRow, canShowDuplicateForRow]);

	const renderProductCell = useCallback((params) => {
		const name = params?.row?.ProductName || params?.row?.productData?.product_name || "-";
		const number = params?.row?.ProductNumber || params?.row?.ProductID || "";
		const imageSrc = params?.row?.imageUrl || `${ASSETS_BASE_URL}/stock.png`;
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
				<Box
					component="img"
					src={imageSrc}
					alt={name}
					onError={(e) => {
						e.target.onerror = null;
						e.target.src = `${ASSETS_BASE_URL}/stock.png`;
					}}
					sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
				/>
				<Box>
					<Typography variant="body2" fontWeight={600}>
						{name}
					</Typography>

				</Box>
			</Box>
		);
	}, [t]);

	const cartColumns = useMemo(
		() => [
			{ field: "ProductName", headerName: t('TABLE_HEADERS.PART_NAME'), flex: 1.5, renderCell: renderProductCell },
			{ field: "ProductNumber", headerName: t('TABLE_HEADERS.PART'), width: 180 },
			{ field: "Quantity", headerName: t('TABLE_HEADERS.QUANTITY'), width: 120 },
		],
		[renderProductCell, t]
	);

	const allocationColumns = useMemo(
		() => [
			{ field: "customer_site_number", headerName: t('TABLE_HEADERS.SITE_NUMBER'), width: 160 },
			{ field: "customer_site_name", headerName: t('LABELS.SITE_NAME'), flex: 1 },
			{ field: "parent_customer_number", headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER'), width: 180 },
			{ field: "parent_customer_name", headerName: t('LABELS.CUSTOMER_NAME'), flex: 1 },
		],
		[t]
	);

	const personalDetailsContent = useMemo(() => {
		if (!detailUser) return null;
		const statusPending = Number(detailUser.user_status) === 0;
		const displayName = detailUser.user_display_name || `${detailUser.user_first_name || ''} ${detailUser.user_last_name || ''}`.trim() || detailUser.user_email;
		const meta = detailUser.user_meta || {};
		const formatValue = (value) => {
			if (value === null || value === undefined) return 'N/A';
			if (typeof value === 'string' && value.trim() === '') return 'N/A';
			return value;
		};
		const sections = [
			{
				title: t('LABELS.PERSONAL_DETAILS'),
				fields: [
					{ label: t('TABLE_HEADERS.NAME'), value: formatValue(displayName) },
					{ label: t('TABLE_HEADERS.EMAIL'), value: formatValue(detailUser.user_email) },
					{ label: t('LABELS.PHONE_NUMBER'), value: formatValue(detailUser.user_phone) },
				],
			},
			{
				title: `${t('LABELS.ROLE')} & ${t('TABLE_HEADERS.STATUS')}`,
				fields: [
					{ label: t('LABELS.ROLE'), value: formatValue(detailUser.user_role_name || detailUser.role_type) },
					{ label: t('TABLE_HEADERS.STATUS'), value: statusPending ? t('MENU_ITEMS.PENDING') : t('MENU_ITEMS.APPROVED'), chip: true, color: statusPending ? 'warning' : 'success' },
				],
			},
			{
				title: t('TITLES.COMPANY_DETAILS'),
				fields: [
					{ label: t('TITLES.COMPANY_NAME'), value: formatValue(detailUser.company_name || meta.company_name) },
					{ label: t('LABELS.STREET_ADDRESS'), value: formatValue(meta.company_street_address) },
					{ label: t('LABELS.CITY'), value: formatValue(meta.company_city) },
					{ label: t('LABELS.STATE_PROVINCE'), value: formatValue(meta.company_state_province) },
					{ label: t('LABELS.REGION'), value: formatValue(detailUser.region || meta.region) },
					{ label: t('LABELS.COUNTRY'), value: formatValue(detailUser.country || meta.country) },
					{ label: t('LABELS.ZIP_POSTAL_CODE'), value: formatValue(meta.company_zip_postal_code) },
				],
			},
		];
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
				{sections.map((section) => (
					<Box key={section.title}>
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
							{section.title}
						</Typography>
						<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: section.fields.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr' }, gap: 2 }}>
							{section.fields.map((field) => (
								<Box key={`${section.title}-${field.label}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
									<Typography variant="caption" color="text.secondary">
										{field.label}
									</Typography>
									{field.chip ? (
										<Chip size="small" color={field.color} label={formatValue(field.value)} variant="outlined" />
									) : (
										<Typography variant="body1" fontWeight={500}>
											{formatValue(field.value)}
										</Typography>
									)}
								</Box>
							))}
						</Box>
					</Box>
				))}
			</Box>
		);
	}, [detailUser, t]);

	const allocationsContent = useMemo(() => {
		if (!detailUser) return null;
		const allocations = Array.isArray(detailUser.allocations) ? detailUser.allocations : [];
		const roleType = String(detailUser?.role_type || detailUser?.user_role_type || '').trim().toLowerCase();
		const roleName = String(detailUser?.user_role_name || detailUser?.user_role || '').trim().toLowerCase();
		const isAdminRole = roleType === 'admin' || roleName === 'admin' || roleName.includes('admin');
		const isApproved = Number(detailUser?.user_status) !== 0;
		if (isAdminRole && isApproved && allocations.length === 0) {
			return (
				<Alert severity="info">
					{t(
						'USER_MANAGEMENT.APPROVED_ADMIN_ALL_LOCATIONS_INFO',
						{ defaultValue: 'This approved admin has access to all sites, so there are no specific allocated sites to list.' }
					)}
				</Alert>
			);
		}
		if (isAdminRole && !isApproved && allocations.length === 0) {
			return (
				<Alert severity="warning">
					{t(
						'USER_MANAGEMENT.PENDING_ADMIN_NO_ALLOCATIONS_INFO',
						{ defaultValue: 'This admin is pending approval and currently has no active site allocations to display.' }
					)}
				</Alert>
			);
		}
		if (allocations.length === 0) {
			return <Alert severity="info">{t('UI_TEXT.NO_ALLOCATED_LOCATIONS')}</Alert>;
		}
		if (!allocationRows.length) return <Alert severity="info">{t('UI_TEXT.NO_ALLOCATED_LOCATIONS')}</Alert>;
		return (
			<Datagrid
				data={allocationRows}
				columns={allocationColumns}
				rowIdField="id"
				showSearchBar={false}
				checkboxSelection={false}
				autoHeight
				hideFilterFromColumnMenu
				lockActiveColumnInManageColumns
				preventHidingLastColumn
			/>
		);
	}, [allocationColumns, allocationRows, detailUser, t]);

	const cartContent = useMemo(() => {
		if (!detailUser) return null;
		const renderSkeletons = () => (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{Array.from({ length: 2 }).map((_, index) => (
					<Box key={`cart-skeleton-${index}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
						<Skeleton variant="text" width="40%" />
						<Skeleton variant="rounded" height={60} sx={{ mt: 1 }} />
						<Skeleton variant="rounded" height={60} sx={{ mt: 1 }} />
					</Box>
				))}
			</Box>
		);
		if (cartLoading) {
			return renderSkeletons();
		}
		if (cartError) {
			return <Alert severity="error">{cartError}</Alert>;
		}
		const allocations = Array.isArray(detailUser.allocations) ? detailUser.allocations : [];
		const fallbackSites = Object.keys(userCartBySite || {}).map((site) => ({ customer_site_number: site, customer_site_name: site }));
		const sectionsSource = allocations.length > 0 ? allocations : fallbackSites;
		const sections = sectionsSource
			.map((allocation, index) => {
				const siteKey = deriveSiteKey(allocation);
				const rows = userCartBySite[siteKey];
				if (!siteKey || !Array.isArray(rows) || rows.length === 0) return null;
				const meta = getSiteMetaByAllocation({
					...allocation,
					customer_site_number: allocation?.customer_site_number || siteKey,
					customer_site_name: allocation?.customer_site_name || allocation?.site_name || siteKey,
					site_id: allocation?.site_id || rows?.[0]?.site_id,
				});
				const totalParts = rows.reduce((sum, item) => sum + Number(item?.Quantity || 0), 0);
				return (
					<Accordion key={`${siteKey}-${index}`} defaultExpanded={index === 0}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
								<Box>
									<Typography variant="subtitle2" fontWeight="bold">
										{meta.customer_site_name || t('LABELS.SITE')}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{meta.customer_site_number || siteKey}
									</Typography>
								</Box>
								<Chip
									color="primary"
									variant="outlined"
									size="small"
									label={`${totalParts} ${t('TABLE_HEADERS.PARTS_QUANTITY') || 'Parts'}`}
								/>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Datagrid
								data={rows}
								columns={cartColumns}
								rowIdField="__id"
								showSearchBar={false}
								checkboxSelection={false}
								autoHeight
								hideFilterFromColumnMenu
								lockActiveColumnInManageColumns
								preventHidingLastColumn
							/>
						</AccordionDetails>
					</Accordion>
				);
			})
			.filter(Boolean);

		if (!sections.length) {
			return <Alert severity="info">{t('TITLES.NO_CART_ITEMS')}</Alert>;
		}
		return sections;
	}, [cartColumns, cartError, cartLoading, detailUser, getSiteMetaByAllocation, t, userCartBySite]);

	useEffect(() => {
		if (!detailUser) {
			setAllocationRows([]);
			return;
		}
		const allocations = Array.isArray(detailUser.allocations) ? detailUser.allocations : [];
		const rows = allocations
			.map((allocation, index) => {
				const meta = getSiteMetaByAllocation(allocation);
				const idSource = meta.customer_site_number || meta.site_id || index;
				return {
					id: `${idSource}-${index}`,
					...meta,
				};
			})
			.filter((row) => row.customer_site_number || row.customer_site_name);
		setAllocationRows(rows);
	}, [detailUser, getSiteMetaByAllocation]);

	const detailTabs = useMemo(() => ([
		{ key: 'details', label: t('UI_TEXT.USER_DETAILS'), content: personalDetailsContent },
		{ key: 'allocations', label: t('TITLES.ALLOCATED_LOCATIONS'), content: allocationsContent },
		{ key: 'cart', label: t('TITLES.CART'), content: cartContent },
	]), [allocationsContent, cartContent, personalDetailsContent, t]);

	const handleUserRowClick = (params) => {
		if (!params?.row) return;
		openUserDetailModal(params.row);
	};
	const selectedDeletableUsersCount = useMemo(
		() => {
			const selectedIdSet = new Set((selectedUsersRows || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)));
			return (activeTabRows || []).filter((row) => selectedIdSet.has(Number(row?.user_id)) && canShowDeleteForRow(row) && Number(row?.user_id) !== Number(authUser?.user_id)).length;
		},
		[activeTabRows, selectedUsersRows, canShowDeleteForRow, authUser?.user_id]
	);

	const customButtons = React.useMemo(() => [
		{
			label: t('GENERAL.REFRESH_USERS'),
			icon: <Refresh />,
			onClick: () => { reloadUsers() },
		},
		...(canCreateUsers ? [{
			label: t('LABELS.CREATE_USER'),
			icon: <AddCircle />,
			onClick: () => {
				setAssignedSitesLoc([]);
				setSelectedUser(null);
				setCreateNewUser(true);
			},
		}] : []),
		...(canDeleteUsers ? [{
			label: t('LABELS.DELETE_SELECTED'),
			icon: <Delete />,
			color: "error",
			onClick: () => {
				handleDeleteRows();
			},
			disabled: selectedDeletableUsersCount < 1,
		}] : []),
	], [t, canCreateUsers, canDeleteUsers, selectedDeletableUsersCount]);

	useEffect(() => {
		return () => {
			if (editFormTimerRef.current) {
				clearTimeout(editFormTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!createNewUser) return;
		const timer = window.setTimeout(() => {
			containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 50);
		return () => window.clearTimeout(timer);
	}, [createNewUser]);

	useEffect(() => {
		setSelectedUsersRows([]);
	}, [activeUsersTab]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.5 }}>
			{showHeader && <HeaderTitle title={t('UI_TEXT.MANAGE_USERS')} subtitle={t('UI_TEXT.ADMIN_CAN_EDIT_REMOVE_USERS')} />}
			<Box className="users-section">
				<Tabs
					value={activeUsersTab}
					onChange={(_, value) => setActiveUsersTab(value)}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ mb: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
				>
					<Tab
						value={USER_LIST_TABS.ALL_USERS}
						label={allUsersTabLabel}
					/>
					<Tab
						value={USER_LIST_TABS.ALL_ADMINS}
						label={allAdminsTabLabel}
					/>
					<Tab
						value={USER_LIST_TABS.USERS_BY_SITES}
						label={usersBySitesTabLabel}
					/>
				</Tabs>
				{activeUsersTab === USER_LIST_TABS.USERS_BY_SITES && (
					<Box
						sx={{
							mb: 2,
							mt: 1,
							display: "flex",
							flexDirection: "column",
							rowGap: '5px',
							background: '#eee',
							p: 2,
							borderRadius: '10px'
						}}
					>
						<Typography variant="body1" fontWeight={"bold"}>{usersBySitesFiltersLabel}</Typography>
						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: 'column', md: 'row' },
								columnGap: "20px",
								rowGap: 1,
							}}
						>
							{usersBySitesControls}
						</Box>
					</Box>
				)}

				{loading ? (
					<DatagridLoading actionButtons={2} columns={5} rows={6} />
				) : activeUsersTab === USER_LIST_TABS.USERS_BY_SITES && !usersBySiteCustomer && !usersBySiteSite ? (
					<Alert severity="info" sx={{ mt: 1 }}>
						{usersBySitesEmptySelectionMessage}
					</Alert>
				) : (
					<Datagrid
								data={activeTabRows}
						columns={columns}
						onSelect={handleRowSelect}
								customButtons={customButtons.flat()}
								rowClick
								onRowClick={handleUserRowClick}
						checkboxSelection={canDeleteUsers}
						pageSize={5}
						rowIdField="user_id"
						sortBy={defaultSort}
								extraFilters={quickFilters}
						customButtonsDataTour="admin-user-actions"
						hideFilterFromColumnMenu
						lockActiveColumnInManageColumns
						preventHidingLastColumn
					/>
				)}
			</Box>
			{canManageUsers && visibilityNote && (
				<>
					<ContentInfo sx={{ mt: activeUsersTab === USER_LIST_TABS.USERS_BY_SITES ? 1.5 : 0 }}>
						<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', columnGap: 1 }}>
							<HelpOutlineOutlined />
							<Box>
								<Typography variant='body1' mb={0.5} fontWeight={550}>{t('USER_MANAGEMENT.PLEASE_NOTE', { defaultValue: 'Please Note:' })}</Typography>
								<Typography variant="body2">{`- ${visibilityNote}`}</Typography>
								<Typography variant="body2" >
									{`- ${t('USER_MANAGEMENT.DUPLICATE_USER_PASSWORD_NOTE', {
										defaultValue: 'Duplicate users are created with no password and a Pending status. There is no default password. Use the password reset action to send a create/reset password link so the duplicated user can set their own password.',
									})}`}
								</Typography>
							</Box>
						</Box>
					</ContentInfo>



				</>
			)}
			<Box sx={{ width: '100%' }} ref={containerRef}>
				{editFormLoading && (
					<Box sx={{ p: 2 }}>
						<Skeleton variant="text" width="30%" height={32} />
						<Skeleton variant="rounded" height={90} sx={{ mt: 2 }} />
						<Skeleton variant="rounded" height={50} sx={{ mt: 2 }} />
						<Skeleton variant="rounded" height={50} sx={{ mt: 2 }} />
					</Box>
				)}
				{selectedUser && !editFormLoading && (
					<CreateEditUserForm
						mode="edit"
						userData={selectedUser}
						setUserData={setSelectedUser}
						allCustomers={allCustomers}
						allSites={allSites}
						assignedSitesLoc={assignedSitesLoc}
						onAssignedSitesChange={setAssignedSitesLoc}
						onSaved={reloadUsers}
						onClose={() => { setSelectedUser(null); setEditFormLoading(false); }}
					/>
				)}
				{createNewUser && (
					<CreateEditUserForm
						mode="create"
						userData={{}}
						setUserData={() => { }}
						allCustomers={allCustomers}
						allSites={allSites}
						assignedSitesLoc={assignedSitesLoc}
						onAssignedSitesChange={setAssignedSitesLoc}
						onSaved={reloadUsers}
						onClose={() => setCreateNewUser(false)}
						contref={createFormRef}
					/>
				)}
			</Box>


			{!canManageUsers && (
				<Alert severity="info" sx={{ my: 2 }}>
					{t('USER_MANAGEMENT.NO_PERMISSION_TO_MANAGE_USERS')}
				</Alert>
			)}

			<ResetPasswordModal
				open={pwdDialogOpen}
				onClose={() => setPwdDialogOpen(false)}
				userId={pwdTargetUser?.user_id}
				defaultSendLink={true}
			/>
			{detailUser && (
				<ModalWrapper
					open={detailModal.open}
					setOpen={(open) => {
						if (!open) closeUserDetailModal();
						else setDetailModal((prev) => ({ ...prev, open }));
					}}
					onClose={closeUserDetailModal}
					title={detailUser.user_display_name || detailUser.user_email}
					subtitle={detailUser.user_email}
					width="70vw"
					content={
						<Box>
							<Tabs
								value={detailModal.tab}
								onChange={(_, value) => setDetailModal((prev) => ({ ...prev, tab: value }))}
								variant="scrollable"
								scrollButtons="auto"
								sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, mb: 2 }}
							>
								{detailTabs.map((tab) => (
									<Tab key={tab.key} label={tab.label} />
								))}
							</Tabs>
							<Box sx={{ minHeight: 200, p: 1 }}>
								{detailTabs[detailModal.tab]?.content || <Typography variant="body2" color="text.secondary">{t('UI_TEXT.LOADING')}</Typography>}
							</Box>
						</Box>
					}
					actions={
						<>
							<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, width: '100%', gap: 2 }}>
								<Button variant="text" onClick={closeUserDetailModal} sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
									{t('BUTTON.CLOSE')}
								</Button>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
									{canShowDeleteForRow(detailUser) && (
										<Button color="error" startIcon={<Delete />} variant='contained' onClick={() => detailUser && handleDeleteSingle(detailUser.user_id, detailUser)}>
											{t('BUTTON.DELETE')}
										</Button>
									)}
									{canShowDuplicateForRow(detailUser) && (
										<Button startIcon={<CopyAllRounded />} variant="contained" color="primary" onClick={() => detailUser && handleDuplicateUser(detailUser)}>
											{t('BUTTON.DUPLICATE_USER')}
										</Button>
									)}
									{canShowEditForRow(detailUser) && (
										<Button variant="contained" onClick={() => { handleEdit(detailUser, true); closeUserDetailModal(); }}>
											{t('UI_TEXT.EDIT_USER')}
										</Button>
									)}
									{canShowEditForRow(detailUser) && (
										<Button variant="outlined" onClick={() => detailUser && handleOpenPasswordModal(detailUser)}>
											{t('TITLES.RESET_USER_PASSWORD')}
										</Button>
									)}
								</Box>
							</Box>
						</>
					}
				/>
			)}
		</Box>
	);
};

export default ManageUsers;
